import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";

import { LendingVaultExpanded, useAbfFetches, useStrategyDurations } from "@bridgesplit/abf-react";
import { DispatchType } from "@bridgesplit/react";

import {
    CollateralForm,
    initialCollateralForm,
    initialRateForm,
    initialSettingsForm,
    RateForm,
    SettingForm
} from "./type";
import { resetAllForms } from "./common";

type ContextState = {
    collateralForm: CollateralForm;
    setCollateralForm: DispatchType<CollateralForm>;
    rateForm: RateForm;
    setRateForm: DispatchType<RateForm>;
    settingsForm: SettingForm;
    setSettingsForm: DispatchType<SettingForm>;
    vaultExpanded: LendingVaultExpanded | undefined;
    reset: () => void;
};

const ManagerViewContext = createContext<ContextState | null>(null);

export function useManagerViewContext() {
    const context = useContext(ManagerViewContext);
    if (!context) {
        throw new Error("useManagerViewContext must be used within a ManagerViewProvider");
    }
    return context;
}

export function ManagerViewProvider({
    children,
    vaultExpanded
}: {
    children: ReactNode;
    vaultExpanded: LendingVaultExpanded | undefined;
}) {
    const [collateralForm, setCollateralForm] = useState<CollateralForm>(initialCollateralForm);
    const [rateForm, setRateForm] = useState<RateForm>(initialRateForm);
    const [settingsForm, setSettingsForm] = useState<SettingForm>(initialSettingsForm);
    const initialized = useRef(false);
    const { resetLendingVaultPublicApi } = useAbfFetches();
    const { strategyDurations } = useStrategyDurations();

    useEffect(() => {
        if (vaultExpanded && !initialized.current) {
            resetAllForms(vaultExpanded, strategyDurations, setCollateralForm, setRateForm, setSettingsForm);
            initialized.current = true;
        }
    }, [vaultExpanded, setCollateralForm, setRateForm, setSettingsForm, strategyDurations]);

    const reset = async () => {
        // First reset the API
        await resetLendingVaultPublicApi();
        initialized.current = false;

        // Note: The parent component will automatically refetch the data
        // through useLendVaultFromParams, which will trigger the useEffect
        // when new vaultExpanded data arrives
    };

    return (
        <ManagerViewContext.Provider
            value={{
                collateralForm,
                setCollateralForm,
                rateForm,
                setRateForm,
                settingsForm,
                setSettingsForm,
                vaultExpanded,
                reset
            }}
        >
            {children}
        </ManagerViewContext.Provider>
    );
}
